<template>
  <tippy
    ref="tippy"
    :arrow="true"
    :trigger="trigger"
    :placement="placement"
    :interactive="interactive"
    :touch="touch"
    theme="tooltip"
    :append-to="appendToParent ? 'parent' : () => appendTarget"
  >
    <slot />
    <template #content>
      <slot name="content" />
    </template>
  </tippy>
</template>

<script setup lang="ts">
import { useTemplateRef } from 'vue'
import { Tippy, type TippyOptions } from 'vue-tippy'

type Props = {
  /**
   * If the tooltip is interactive (e.g. contains buttons)
   */
  interactive?: boolean
  trigger?: TippyOptions['trigger']
  placement?: TippyOptions['placement']
  touch?: TippyOptions['touch']
  appendToParent?: boolean
}

withDefaults(defineProps<Props>(), {
  interactive: false,
  appendToParent: false,
  placement: 'auto',
  trigger: 'mouseenter focus',
  touch: true
})

const tippyRef = useTemplateRef<typeof Tippy>('tippy')
const appendTarget = document.body

defineExpose({
  show: () => {
    tippyRef.value?.show()
  },
  hide: () => {
    tippyRef.value?.hide()
  }
})
</script>

<style>
@import 'tippy.js/dist/tippy.css';

.tippy-box[data-theme~='tooltip'] {
  @apply font-body text-sm rounded text-n-400 bg-n-50;
}

.tippy-box[data-theme~='tooltip'][data-placement^='top'] > .tippy-arrow:before {
  @apply border-t-n-50;
}

.tippy-box[data-theme~='tooltip'][data-placement^='bottom'] > .tippy-arrow:before {
  @apply border-b-n-50;
}

.tippy-box[data-theme~='tooltip'][data-placement^='left'] > .tippy-arrow:before {
  @apply border-l-n-50;
}

.tippy-box[data-theme~='tooltip'][data-placement^='right'] > .tippy-arrow:before {
  @apply border-r-n-50;
}

.tippy-box[data-theme~='tooltip'] > .tippy-backdrop {
  @apply bg-none;
}

.tippy-box[data-theme~='tooltip'] .tippy-content {
  @apply p-2;
}

/** Dark mode */
.tippy-box[data-theme~='tooltip']:where(.dark, .dark *) {
  @apply bg-n-600 text-n-50;
}

.tippy-box[data-theme~='tooltip'][data-placement^='top']:where(.dark, .dark *)
  > .tippy-arrow:before {
  @apply border-t-n-600;
}

.tippy-box[data-theme~='tooltip'][data-placement^='bottom']:where(.dark, .dark *)
  > .tippy-arrow:before {
  @apply border-b-n-600;
}

.tippy-box[data-theme~='tooltip'][data-placement^='left']:where(.dark, .dark *)
  > .tippy-arrow:before {
  @apply border-l-n-600;
}

.tippy-box[data-theme~='tooltip'][data-placement^='right']:where(.dark, .dark *)
  > .tippy-arrow:before {
  @apply border-r-n-600;
}
</style>
